// src/services/locationTrackingService.ts
import { doc, collection, addDoc, Timestamp } from 'firebase/firestore';
import { db, auth } from './firebase';

// Geohash encoding characters
const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';

/**
 * Encode latitude/longitude to geohash string
 * Precision 9 = ~4.77m accuracy, 8 = ~38m, 7 = ~153m
 */
function encodeGeohash(lat: number, lng: number, precision: number = 9): string {
  let idx = 0;
  let bit = 0;
  let evenBit = true;
  let geohash = '';
  
  let latMin = -90, latMax = 90;
  let lngMin = -180, lngMax = 180;
  
  while (geohash.length < precision) {
    if (evenBit) {
      const lngMid = (lngMin + lngMax) / 2;
      if (lng >= lngMid) {
        idx = idx * 2 + 1;
        lngMin = lngMid;
      } else {
        idx = idx * 2;
        lngMax = lngMid;
      }
    } else {
      const latMid = (latMin + latMax) / 2;
      if (lat >= latMid) {
        idx = idx * 2 + 1;
        latMin = latMid;
      } else {
        idx = idx * 2;
        latMax = latMid;
      }
    }
    evenBit = !evenBit;
    
    if (++bit === 5) {
      geohash += BASE32[idx];
      bit = 0;
      idx = 0;
    }
  }
  
  return geohash;
}

/**
 * Calculate distance between two points using Haversine formula
 * Returns distance in meters
 */
function haversineDistance(
  lat1: number, lng1: number,
  lat2: number, lng2: number
): number {
  const R = 6371000; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

interface Breadcrumb {
  geohash: string;
  lat: number;
  lng: number;
  accuracy: number;
  altitude?: number;
  speed?: number;
  heading?: number;
  timestamp: Timestamp;
  sessionId: string;
  conversationId?: string;
}

interface TrackingConfig {
  minDistance: number;        // Minimum distance in meters before logging (default: 50)
  minAccuracy: number;        // Minimum accuracy required in meters (default: 100)
  geohashPrecision: number;   // Geohash precision (default: 9 = ~4.77m)
  highAccuracy: boolean;      // Use high accuracy GPS (default: true)
  maxAge: number;             // Maximum age of cached position in ms (default: 10000)
  timeout: number;            // Position request timeout in ms (default: 30000)
}

const DEFAULT_CONFIG: TrackingConfig = {
  minDistance: 50,
  minAccuracy: 100,
  geohashPrecision: 9,
  highAccuracy: true,
  maxAge: 10000,
  timeout: 30000,
};

class LocationTrackingService {
  private watchId: number | null = null;
  private lastPosition: { lat: number; lng: number } | null = null;
  private sessionId: string;
  private conversationId: string | null = null;
  private config: TrackingConfig;
  private isRunning: boolean = false;
  private pendingWrite: Promise<void> | null = null;
  private writeQueue: Breadcrumb[] = [];
  private flushTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(config: Partial<TrackingConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Start background location tracking
   */
  start(): boolean {
    if (this.isRunning) {
      console.log('[LocationTracking] Already running');
      return true;
    }

    if (!navigator.geolocation) {
      console.error('[LocationTracking] Geolocation not supported');
      return false;
    }

    if (!auth.currentUser) {
      console.error('[LocationTracking] No authenticated user');
      return false;
    }

    // Generate new session ID for this tracking session
    this.sessionId = this.generateSessionId();
    this.lastPosition = null;

    console.log('[LocationTracking] Starting tracking, session:', this.sessionId);

    this.watchId = navigator.geolocation.watchPosition(
      this.handlePosition.bind(this),
      this.handleError.bind(this),
      {
        enableHighAccuracy: this.config.highAccuracy,
        maximumAge: this.config.maxAge,
        timeout: this.config.timeout,
      }
    );

    this.isRunning = true;
    return true;
  }

  /**
   * Stop background location tracking
   */
  stop(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    
    // Flush any pending writes
    this.flushQueue();
    
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }

    this.isRunning = false;
    console.log('[LocationTracking] Stopped tracking');
  }

  /**
   * Check if tracking is currently active
   */
  isTracking(): boolean {
    return this.isRunning;
  }

  /**
   * Get current session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Get current conversation ID
   */
  getConversationId(): string | null {
    return this.conversationId;
  }

  /**
   * Set the current conversation ID for breadcrumb association
   * Call this when user starts or switches conversations
   */
  setConversationId(conversationId: string | null): void {
    this.conversationId = conversationId;
    console.log('[LocationTracking] Conversation set:', conversationId);
  }

  /**
   * Update tracking configuration
   */
  updateConfig(config: Partial<TrackingConfig>): void {
    this.config = { ...this.config, ...config };
    
    // Restart tracking if running to apply new config
    if (this.isRunning) {
      this.stop();
      this.start();
    }
  }

  private handlePosition(position: GeolocationPosition): void {
    const { latitude, longitude, accuracy, altitude, speed, heading } = position.coords;
    
    // Skip if accuracy is too poor
    if (accuracy > this.config.minAccuracy) {
      console.log('[LocationTracking] Skipping - accuracy too low:', accuracy);
      return;
    }

    // Check distance from last position
    if (this.lastPosition) {
      const distance = haversineDistance(
        this.lastPosition.lat, this.lastPosition.lng,
        latitude, longitude
      );

      if (distance < this.config.minDistance) {
        // Haven't moved enough, skip
        return;
      }
    }

    // Update last position
    this.lastPosition = { lat: latitude, lng: longitude };

    // Create breadcrumb
    const breadcrumb: Breadcrumb = {
      geohash: encodeGeohash(latitude, longitude, this.config.geohashPrecision),
      lat: latitude,
      lng: longitude,
      accuracy: Math.round(accuracy),
      timestamp: Timestamp.now(),
      sessionId: this.sessionId,
    };

    // Add conversation ID if set
    if (this.conversationId) {
      breadcrumb.conversationId = this.conversationId;
    }

    // Add optional fields if available
    if (altitude !== null) breadcrumb.altitude = Math.round(altitude);
    if (speed !== null && speed >= 0) breadcrumb.speed = Math.round(speed * 10) / 10;
    if (heading !== null && heading >= 0) breadcrumb.heading = Math.round(heading);

    // Queue for batch write
    this.queueBreadcrumb(breadcrumb);
  }

  private handleError(error: GeolocationPositionError): void {
    console.error('[LocationTracking] Error:', error.code, error.message);
    
    // Don't stop tracking on temporary errors
    if (error.code === error.PERMISSION_DENIED) {
      this.stop();
    }
  }

  private queueBreadcrumb(breadcrumb: Breadcrumb): void {
    this.writeQueue.push(breadcrumb);

    // Debounce writes - flush after 2 seconds of no new breadcrumbs
    // or immediately if queue reaches 10 items
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
    }

    if (this.writeQueue.length >= 10) {
      this.flushQueue();
    } else {
      this.flushTimeout = setTimeout(() => this.flushQueue(), 2000);
    }
  }

  private async flushQueue(): Promise<void> {
    if (this.writeQueue.length === 0) return;
    if (!auth.currentUser) return;

    // Take current queue and clear it
    const toWrite = [...this.writeQueue];
    this.writeQueue = [];

    // Wait for any pending write to complete
    if (this.pendingWrite) {
      await this.pendingWrite;
    }

    this.pendingWrite = this.writeBreadcrumbs(toWrite);
    await this.pendingWrite;
    this.pendingWrite = null;
  }

  private async writeBreadcrumbs(breadcrumbs: Breadcrumb[]): Promise<void> {
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    const breadcrumbsRef = collection(db, 'users', userId, 'breadcrumbs');

    try {
      // Write each breadcrumb as a separate document
      // Using Promise.all for parallel writes
      await Promise.all(
        breadcrumbs.map(breadcrumb => 
          addDoc(breadcrumbsRef, breadcrumb)
        )
      );

      console.log(`[LocationTracking] Wrote ${breadcrumbs.length} breadcrumb(s)`);
    } catch (error) {
      console.error('[LocationTracking] Failed to write breadcrumbs:', error);
      // Re-queue failed writes
      this.writeQueue.unshift(...breadcrumbs);
    }
  }
}

// Singleton instance
let trackingService: LocationTrackingService | null = null;

/**
 * Get the location tracking service singleton
 */
export function getLocationTrackingService(config?: Partial<TrackingConfig>): LocationTrackingService {
  if (!trackingService) {
    trackingService = new LocationTrackingService(config);
  } else if (config) {
    trackingService.updateConfig(config);
  }
  return trackingService;
}

/**
 * Start location tracking (convenience function)
 */
export function startLocationTracking(config?: Partial<TrackingConfig>): boolean {
  return getLocationTrackingService(config).start();
}

/**
 * Stop location tracking (convenience function)
 */
export function stopLocationTracking(): void {
  if (trackingService) {
    trackingService.stop();
  }
}

/**
 * Check if location tracking is active (convenience function)
 */
export function isLocationTrackingActive(): boolean {
  return trackingService?.isTracking() ?? false;
}

/**
 * Set the current conversation ID for breadcrumb association (convenience function)
 */
export function setTrackingConversationId(conversationId: string | null): void {
  if (trackingService) {
    trackingService.setConversationId(conversationId);
  }
}

export { LocationTrackingService };
export type { TrackingConfig, Breadcrumb };